import { createClient } from '@supabase/supabase-js';

// Define fallback values if env variables are not available
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://fxjyyqdntzslxnqfcelf.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZ4anl5cWRudHpzbHhucWZjZWxmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM2NzMxNTMsImV4cCI6MjA1OTI0OTE1M30.Dv1MFUrcW-YWIUpzsyXHb65El0VnM4kEQvbf_RZvDcI';

// Create and export the Supabase client with explicit auth configuration
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: localStorage,
  }
});

// Enhanced Supabase fetch wrapper with better debugging
export const fetchWithLogging = async (tableName, query) => {
  console.log(`Fetching from ${tableName} with query:`, query);
  try {
    const result = await query;
    console.log(`Result from ${tableName}:`, result);
    return result;
  } catch (error) {
    console.error(`Error fetching from ${tableName}:`, error);
    throw error;
  }
};

// This function checks and fixes the session if needed
export const verifyAndFixSession = async (): Promise<boolean> => {
  try {
    console.log('[verifyAndFixSession] Verifying session...');
    
    // Check for existing session first
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('[verifyAndFixSession] Error getting session:', sessionError);
      return false;
    }
    
    if (!sessionData.session) {
      console.log('[verifyAndFixSession] No existing session found');
      return false;
    }
    
    // Session exists, refresh to ensure it's valid
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
    
    if (refreshError) {
      console.error('[verifyAndFixSession] Error refreshing session:', refreshError);
      
      // If refresh failed, try again with a small timeout
      return new Promise(resolve => {
        setTimeout(async () => {
          try {
            const { data, error } = await supabase.auth.refreshSession();
            if (!error && data.session) {
              console.log('[verifyAndFixSession] Session refreshed successfully after retry');
              sessionStorage.setItem('session-verified-at', Date.now().toString());
              resolve(true);
            } else {
              console.error('[verifyAndFixSession] Failed to refresh session after retry');
              resolve(false);
            }
          } catch (err) {
            console.error('[verifyAndFixSession] Error in retry:', err);
            resolve(false);
          }
        }, 300);
      });
    }
    
    if (refreshData.session) {
      console.log('[verifyAndFixSession] Session refreshed successfully');
      sessionStorage.setItem('session-verified-at', Date.now().toString());
      return true;
    } else {
      console.log('[verifyAndFixSession] Refresh successful but no session returned');
      return false;
    }
  } catch (error) {
    console.error('[verifyAndFixSession] Unexpected error during verification:', error);
    return false;
  }
};

// Global handler for session synchronization across tabs
export const setupSessionSync = () => {
  try {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'guia-vegana-supabase-auth' || e.key === 'supabase.auth.token') {
        console.log('[setupSessionSync] Auth storage changed in another tab');
        
        // Avoid conflicts by waiting a small amount of time
        setTimeout(() => {
          supabase.auth.refreshSession().then(({ data, error }) => {
            if (error) {
              console.warn('[setupSessionSync] Error refreshing session:', error);
              verifyAndFixSession();
            } else {
              console.log('[setupSessionSync] Session refreshed after storage change');
              sessionStorage.setItem('session-verified-at', Date.now().toString());
            }
          });
        }, 500);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Custom event for explicit synchronization
    window.addEventListener('session-sync-request', () => {
      console.log('[setupSessionSync] Session sync requested');
      supabase.auth.refreshSession().then(({ data, error }) => {
        if (!error && data.session) {
          console.log('[setupSessionSync] Session synced successfully');
          sessionStorage.setItem('session-verified-at', Date.now().toString());
          
          // Notify other tabs that session was synced
          try {
            localStorage.setItem('session-last-synced', Date.now().toString());
          } catch (err) {
            console.error('[setupSessionSync] Error setting sync timestamp:', err);
          }
        }
      });
    });
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('session-sync-request', handleStorageChange);
    };
  } catch (error) {
    console.error('[setupSessionSync] Error setting up session sync:', error);
    return () => {};
  }
};

// Challenge Functions
export const getChallengeProgress = async (userId: string, challengeId: number) => {
  try {
    console.log(`[CHALLENGE] Fetching challenge progress for user ${userId}, challenge ${challengeId}`);
    
    const { data, error } = await supabase
      .from('user_challenges')
      .select('*')
      .eq('user_id', userId)
      .eq('challenge_id', challengeId)
      .single();
      
    if (error) {
      if (error.code === 'PGRST116') {
        // No data found - this is expected for challenges that haven't been started
        console.log(`[CHALLENGE] No data found for challenge ${challengeId} (expected for new challenges)`);
        return null;
      }
      console.error(`[CHALLENGE] Error fetching challenge progress for challenge ${challengeId}:`, error);
      return null;
    }
    
    console.log(`[CHALLENGE] Found progress data for challenge ${challengeId}:`, data);
    return data;
  } catch (error) {
    console.error(`[CHALLENGE] Error in getChallengeProgress for challenge ${challengeId}:`, error);
    return null;
  }
};

export const getNextAvailableChallenge = async (userId: string) => {
  try {
    console.log(`[CHALLENGE] Getting next available challenge for user ${userId}`);
    
    const { data, error } = await supabase
      .rpc('get_next_challenge', { p_user_id: userId });
      
    if (error) {
      console.error("[CHALLENGE] Error getting next challenge:", error);
      return 1; // Default to first challenge
    }
    
    console.log(`[CHALLENGE] Next available challenge: ${data || 1}`);
    return data || 1;
  } catch (error) {
    console.error("[CHALLENGE] Error in getNextAvailableChallenge:", error);
    return 1;
  }
};

export const startChallenge = async (userId: string, challengeId: number, initialProgress: any = {}) => {
  try {
    console.log(`[CHALLENGE] Starting challenge ${challengeId} for user ${userId} with initial progress:`, initialProgress);
    
    // Check if the challenge already exists
    const existingChallenge = await getChallengeProgress(userId, challengeId);
    if (existingChallenge) {
      console.log(`[CHALLENGE] Challenge ${challengeId} already exists, updating instead of inserting`);
      return await updateChallengeProgress(userId, challengeId, initialProgress);
    }
    
    // Save to sessionStorage as well for redundancy
    const sessionChallengeKey = `challenge_state_${userId}_${challengeId}`;
    const challengeState = {
      status: "in_progress",
      startedAt: initialProgress.started_at || new Date().toISOString(),
      currentValue: initialProgress.currentValue || 0
    };
    sessionStorage.setItem(sessionChallengeKey, JSON.stringify(challengeState));
    localStorage.setItem(sessionChallengeKey, JSON.stringify(challengeState));
    
    const { data, error } = await supabase
      .from('user_challenges')
      .insert({
        user_id: userId,
        challenge_id: challengeId,
        progress: initialProgress
      });
      
    if (error) {
      console.error(`[CHALLENGE] Error starting challenge ${challengeId}:`, error);
      return false;
    }
    
    console.log(`[CHALLENGE] Successfully started challenge ${challengeId}`);
    
    // Store in localStorage for redundancy
    const localStorageKey = `challenge_${challengeId}_${userId}`;
    localStorage.setItem(localStorageKey, JSON.stringify({
      user_id: userId,
      challenge_id: challengeId,
      started_at: initialProgress.started_at || new Date().toISOString(),
      progress: initialProgress
    }));
    
    return true;
  } catch (error) {
    console.error(`[CHALLENGE] Error in startChallenge for challenge ${challengeId}:`, error);
    return false;
  }
};

export const updateChallengeProgress = async (userId: string, challengeId: number, progress: any) => {
  try {
    console.log(`[CHALLENGE] Updating challenge ${challengeId} progress for user ${userId}:`, progress);
    
    // Update sessionStorage as well
    const sessionChallengeKey = `challenge_state_${userId}_${challengeId}`;
    const existingSessionState = sessionStorage.getItem(sessionChallengeKey);
    
    if (existingSessionState) {
      try {
        const parsedState = JSON.parse(existingSessionState);
        let targetValue = challengeId === 3 ? 5 : 3; // 5 for likes challenge, 3 for others
        
        const updatedState = {
          ...parsedState,
          currentValue: progress.currentValue !== undefined ? progress.currentValue : parsedState.currentValue,
          progress: progress.currentValue !== undefined ? Math.min((progress.currentValue / targetValue) * 100, 100) : parsedState.progress
        };
        
        sessionStorage.setItem(sessionChallengeKey, JSON.stringify(updatedState));
        localStorage.setItem(sessionChallengeKey, JSON.stringify(updatedState));
        console.log(`[CHALLENGE] Updated session state for challenge ${challengeId}:`, updatedState);
      } catch (e) {
        console.error(`[CHALLENGE] Error updating session state for challenge ${challengeId}:`, e);
      }
    } else if (progress.started_at) {
      // If no session state exists but progress contains started_at, create a new one
      let targetValue = challengeId === 3 ? 5 : 3; // 5 for likes challenge, 3 for others
      
      const newState = {
        status: "in_progress",
        startedAt: progress.started_at,
        currentValue: progress.currentValue || 0,
        progress: progress.currentValue !== undefined ? Math.min((progress.currentValue / targetValue) * 100, 100) : 0
      };
      
      sessionStorage.setItem(sessionChallengeKey, JSON.stringify(newState));
      localStorage.setItem(sessionChallengeKey, JSON.stringify(newState));
      console.log(`[CHALLENGE] Created new session state for challenge ${challengeId}:`, newState);
    }
    
    // First check if the challenge exists
    const existingChallenge = await getChallengeProgress(userId, challengeId);
    
    if (!existingChallenge) {
      console.log(`[CHALLENGE] Challenge ${challengeId} doesn't exist yet, creating it instead of updating`);
      return await startChallenge(userId, challengeId, progress);
    }
    
    const { data, error } = await supabase
      .from('user_challenges')
      .update({
        progress
      })
      .eq('user_id', userId)
      .eq('challenge_id', challengeId);
      
    if (error) {
      console.error(`[CHALLENGE] Error updating challenge ${challengeId} progress:`, error);
      return false;
    }
    
    console.log(`[CHALLENGE] Successfully updated challenge ${challengeId} progress`);
    return true;
  } catch (error) {
    console.error(`[CHALLENGE] Error in updateChallengeProgress for challenge ${challengeId}:`, error);
    return false;
  }
};

export const completeChallenge = async (userId: string, challengeId: number, finalProgress: any) => {
  try {
    console.log(`[CHALLENGE] Completing challenge ${challengeId} for user ${userId} with final progress:`, finalProgress);
    
    // Update sessionStorage to show completion
    const sessionChallengeKey = `challenge_state_${userId}_${challengeId}`;
    const completedState = {
      status: "completed",
      startedAt: finalProgress.started_at || new Date().toISOString(),
      currentValue: finalProgress.currentValue || 3,
      progress: 100
    };
    
    sessionStorage.setItem(sessionChallengeKey, JSON.stringify(completedState));
    localStorage.setItem(sessionChallengeKey, JSON.stringify(completedState));
    
    const { data, error } = await supabase
      .from('user_challenges')
      .update({
        completed_at: new Date().toISOString(),
        progress: finalProgress
      })
      .eq('user_id', userId)
      .eq('challenge_id', challengeId);
      
    if (error) {
      console.error(`[CHALLENGE] Error completing challenge ${challengeId}:`, error);
      return false;
    }
    
    console.log(`[CHALLENGE] Successfully completed challenge ${challengeId}`);
    
    // Add points to the user after completing a challenge
    // Different challenges can award different points
    let pointsToAward = 20; // Default points for a challenge
    
    // Award specific points based on challenge ID
    if (challengeId === 1) {
      pointsToAward = 25; // First challenge awards 25 points
    } else if (challengeId === 2) {
      pointsToAward = 30; // Second challenge awards 30 points
    } else if (challengeId === 3) {
      pointsToAward = 35; // Third challenge awards 35 points
    }
    
    // Update user points right after completing the challenge
    const pointsUpdated = await updateUserPoints(userId, pointsToAward);
    
    if (pointsUpdated) {
      console.log(`[CHALLENGE] Awarded ${pointsToAward} points to user ${userId} for completing challenge ${challengeId}`);
      
      // Dispatch a challenge-completed event with points information
      try {
        const challengeCompletedEvent = new CustomEvent('challenge-completed', {
          detail: { 
            challengeId,
            userId,
            points: pointsToAward
          }
        });
        window.dispatchEvent(challengeCompletedEvent);
        console.log(`[CHALLENGE] Dispatched challenge-completed event for challenge ${challengeId}`);
      } catch (e) {
        console.error("[CHALLENGE] Error dispatching challenge-completed event:", e);
      }
    } else {
      console.error(`[CHALLENGE] Failed to award points for challenge ${challengeId}`);
    }
    
    return true;
  } catch (error) {
    console.error(`[CHALLENGE] Error in completeChallenge for challenge ${challengeId}:`, error);
    return false;
  }
};

export const updateUserPoints = async (userId: string, points: number) => {
  try {
    console.log(`[CHALLENGE] Updating user points: Adding ${points} points to user ${userId}`);
    
    // Add a delay to avoid race conditions
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('points')
      .eq('id', userId)
      .single();
    
    if (fetchError) {
      console.error("[CHALLENGE] Error fetching user profile:", fetchError);
      return false;
    }
    
    const currentPoints = profile?.points || 0;
    const newPoints = currentPoints + points;
    
    console.log(`[CHALLENGE] Current points: ${currentPoints}, New points after adding ${points}: ${newPoints}`);
    
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ points: newPoints })
      .eq('id', userId);
      
    if (updateError) {
      console.error("[CHALLENGE] Error updating user points:", updateError);
      return false;
    }
    
    console.log(`[CHALLENGE] Successfully updated user points to ${newPoints}`);
    
    // Update the UserLevel component by dispatching a custom event
    try {
      const pointsUpdatedEvent = new CustomEvent('points-updated', {
        detail: { userId, newPoints }
      });
      window.dispatchEvent(pointsUpdatedEvent);
      console.log(`[CHALLENGE] Dispatched points-updated event with ${newPoints} points`);
      
      // Also save to localStorage for redundancy
      localStorage.setItem(`user_points_${userId}`, newPoints.toString());
    } catch (e) {
      console.error("[CHALLENGE] Error dispatching points-updated event:", e);
    }
    
    return true;
  } catch (error) {
    console.error("[CHALLENGE] Error in updateUserPoints:", error);
    return false;
  }
};

// Login Streak Function
export const recordLoginAndGetStreak = (userId: string): number => {
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
  const storageKey = `login_streak_${userId}`;
  const lastLoginKey = `last_login_${userId}`;
  
  // Get current streak and last login date
  const currentStreak = parseInt(localStorage.getItem(storageKey) || '0', 10);
  const lastLoginDate = localStorage.getItem(lastLoginKey);
  
  console.log('Streak calculation:', { today, lastLoginDate, currentStreak });
  
  // If this is the first login ever
  if (!lastLoginDate) {
    console.log('First login ever, setting streak to 1');
    localStorage.setItem(storageKey, '1');
    localStorage.setItem(lastLoginKey, today);
    return 1;
  }
  
  // If already logged in today, return current streak
  if (lastLoginDate === today) {
    console.log('Already logged in today, returning current streak:', currentStreak);
    return currentStreak;
  }
  
  // Calculate days difference
  const lastDate = new Date(lastLoginDate);
  const todayDate = new Date(today);
  const diffTime = todayDate.getTime() - lastDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  console.log('Days difference:', diffDays);
  
  let newStreak: number;
  
  if (diffDays === 1) {
    // Consecutive day - increment streak, but cap at 7
    newStreak = Math.min(currentStreak + 1, 7);
    console.log('Consecutive day, new streak:', newStreak);
  } else if (diffDays > 1) {
    // Missed days - reset streak to 1
    newStreak = 1;
    console.log('Missed days, resetting streak to 1');
  } else {
    // This shouldn't happen (diffDays < 1 means future date)
    newStreak = currentStreak;
    console.log('Unexpected case, keeping current streak:', newStreak);
  }
  
  // Save new streak and today's date
  localStorage.setItem(storageKey, newStreak.toString());
  localStorage.setItem(lastLoginKey, today);
  
  console.log('Final streak recorded:', newStreak);
  return newStreak;
};

// Improved function to count likes more accurately
export const getActualLikesCount = async (userId: string) => {
  try {
    // For demo users, check localStorage
    if (userId.startsWith('user-')) {
      const storedLikes = localStorage.getItem('recipe-likes');
      if (storedLikes) {
        try {
          const likesData = JSON.parse(storedLikes);
          let count = 0;
          
          // Count all likes associated with this user
          for (const recipeId in likesData) {
            if (likesData[recipeId] === userId) {
              count++;
            }
          }
          
          // Alternative method: if the structure is different and likes are stored by user ID
          const userLikes = likesData[userId] || [];
          if (Array.isArray(userLikes)) {
            return userLikes.length;
          }
          
          return count;
        } catch (e) {
          console.error("Error parsing stored likes:", e);
          return 0;
        }
      }
      
      // Try checking from a different localStorage format
      const favoritesKey = 'guia-vegana-favorites';
      const favorites = localStorage.getItem(favoritesKey);
      if (favorites) {
        try {
          const parsedFavorites = JSON.parse(favorites);
          if (parsedFavorites[userId] && Array.isArray(parsedFavorites[userId])) {
            return parsedFavorites[userId].length;
          }
        } catch (e) {
          console.error("Error parsing favorites:", e);
        }
      }
      
      return 0;
    }
    
    // For real users, query the database
    const { data, error, count } = await supabase
      .from('recipe_likes')
      .select('*', { count: 'exact' })
      .eq('user_id', userId);
      
    if (error) {
      console.error("Error counting likes:", error);
      return 0;
    }
    
    return count || (data?.length || 0);
  } catch (error) {
    console.error("Error in getActualLikesCount:", error);
    return 0;
  }
};

// Debug utility for profile fetching with better error handling
export const getProfileWithLogging = async (userId: string) => {
  try {
    console.log(`Fetching profile for user: ${userId}`);
    
    // Check if userId is valid
    if (!userId || typeof userId !== 'string') {
      console.error("Invalid user ID provided:", userId);
      return null;
    }
    
    const { data, error } = await supabase
      .from('profiles')
      .select('id, name, username, avatar_url, points, favorites')
      .eq('id', userId)
      .single();
      
    if (error) {
      console.error("Error fetching profile:", error);
      
      // Try a more limited query if the first one fails
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('profiles')
        .select('id, name, username, avatar_url')
        .eq('id', userId)
        .single();
        
      if (fallbackError) {
        console.error("Fallback profile fetch also failed:", fallbackError);
        return {
          id: userId,
          name: 'Usuario',
          username: null,
          avatar_url: null,
          points: 0
        };
      }
      
      // Add default points value to fallback data
      return {
        ...fallbackData,
        points: 0
      };
    }
    
    console.log(`Profile data for ${userId}:`, data);
    // Ensure points has a default value
    return {
      ...data,
      points: data.points || 0
    };
  } catch (error) {
    console.error("Error in getProfileWithLogging:", error);
    // Return a basic profile with default values
    return {
      id: userId,
      name: 'Usuario',
      username: null,
      avatar_url: null,
      points: 0
    };
  }
};

// Enhanced function to track comments for challenges
export const trackCommentForChallenge = async (userId: string, commentData: { recipeId: string, commentId: string, timestamp: string }) => {
  try {
    if (!userId) {
      console.error("[COMMENT CHALLENGE] No user ID provided for trackCommentForChallenge");
      return false;
    }
    
    console.log(`[COMMENT CHALLENGE] Tracking comment for challenge. User: ${userId}`, commentData);
    
    // Store in localStorage for immediate access
    const commentTrackingKey = `challenge_comments_${userId}`;
    let commentsList = [];
    
    try {
      const storedComments = localStorage.getItem(commentTrackingKey);
      if (storedComments) {
        commentsList = JSON.parse(storedComments);
        // Validate that it's an array
        if (!Array.isArray(commentsList)) {
          console.error("[COMMENT CHALLENGE] Stored comments is not an array, resetting");
          commentsList = [];
        }
      }
    } catch (e) {
      console.error("[COMMENT CHALLENGE] Error parsing stored comments:", e);
    }
    
    // Add the new comment to the list
    commentsList.push(commentData);
    
    // Store the updated list
    localStorage.setItem(commentTrackingKey, JSON.stringify(commentsList));
    
    console.log(`[COMMENT CHALLENGE] Updated challenge comments for user ${userId}. Total: ${commentsList.length}`);
    
    // Update session state
    const sessionChallengeKey = `challenge_state_${userId}_2`;
    try {
      const existingState = sessionStorage.getItem(sessionChallengeKey);
      if (existingState) {
        const parsedState = JSON.parse(existingState);
        if (parsedState.status === "in_progress") {
          const updatedState = {
            ...parsedState,
            currentValue: commentsList.length,
            progress: Math.min((commentsList.length / 3) * 100, 100)
          };
          sessionStorage.setItem(sessionChallengeKey, JSON.stringify(updatedState));
          localStorage.setItem(sessionChallengeKey, JSON.stringify(updatedState));
          console.log(`[COMMENT CHALLENGE] Updated session state: ${JSON.stringify(updatedState)}`);
        }
      }
    } catch (e) {
      console.error("[COMMENT CHALLENGE] Error updating session state:", e);
    }
    
    // Dispatch an event to notify other components about the comment
    const commentAddedEvent = new CustomEvent('comment-added', {
      detail: {
        userId,
        commentData,
        commentCount: commentsList.length
      }
    });
    window.dispatchEvent(commentAddedEvent);
    
    // Get the current challenge progress
    const challengeData = await getChallengeProgress(userId, 2);
    if (challengeData) {
      // Make sure we update the database progress
      const currentProgress = challengeData.progress || {};
      const updatedProgress = {
        ...currentProgress,
        currentValue: commentsList.length,
        commentIds: commentsList.map(c => c.commentId)
      };
      
      // Update the challenge progress in the database
      await updateChallengeProgress(userId, 2, updatedProgress);
      console.log(`[COMMENT CHALLENGE] Updated challenge 2 progress in database:`, updatedProgress);
    } else {
      // Challenge doesn't exist yet, start it
      const initialProgress = {
        currentValue: commentsList.length,
        commentIds: commentsList.map(c => c.commentId),
        started_at: new Date().toISOString()
      };
      
      // Start the challenge with the initial progress
      await startChallenge(userId, 2, initialProgress);
      console.log(`[COMMENT CHALLENGE] Started challenge 2 with initial progress:`, initialProgress);
    }
    
    return true;
  } catch (error) {
    console.error("[COMMENT CHALLENGE] Error tracking comment for challenge:", error);
    return false;
  }
};

// Improved function to count comments more accurately for challenges
export const getCommentsCountWithinPeriod = async (userId: string, startDate: Date, endDate: Date) => {
  try {
    if (!userId) {
      console.error("[COMMENT CHALLENGE] No user ID provided for getCommentsCountWithinPeriod");
      return 0;
    }
    
    console.log(`[COMMENT CHALLENGE] Counting comments for user ${userId} from ${startDate.toISOString()} to ${endDate.toISOString()}`);
    
    // Get comments from localStorage tracking
    const commentTrackingKey = `challenge_comments_${userId}`;
    let commentsList = [];
    
    try {
      const storedComments = localStorage.getItem(commentTrackingKey);
      console.log("[COMMENT CHALLENGE] Raw stored comments:", storedComments);
      
      if (storedComments) {
        const allComments = JSON.parse(storedComments);
        if (Array.isArray(allComments)) {
          console.log("[COMMENT CHALLENGE] All stored comments:", allComments);
          commentsList = allComments;
        } else {
          console.error("[COMMENT CHALLENGE] Stored comments is not an array:", allComments);
        }
      } else {
        console.log("[COMMENT CHALLENGE] No stored comments found in localStorage");
      }
    } catch (e) {
      console.error("[COMMENT CHALLENGE] Error parsing stored comments:", e);
    }
    
    // For real users, also query the database
    let dbCount = 0;
    
    try {
      const { data, error, count } = await supabase
        .from('recipe_comments')
        .select('id', { count: 'exact', head: false })
        .eq('user_id', userId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
        
      if (error) {
        console.error("[COMMENT CHALLENGE] Error counting comments from database:", error);
      } else {
        dbCount = count !== null ? count : (data?.length || 0);
        console.log(`[COMMENT CHALLENGE] Found ${dbCount} comments in database`);
      }
    } catch (dbError) {
      console.error("[COMMENT CHALLENGE] Database error counting comments:", dbError);
    }
    
    // Check if we have any progress saved in the challenge
    try {
      const challengeData = await getChallengeProgress(userId, 2);
      console.log(`[COMMENT CHALLENGE] Challenge data from database:`, challengeData);
      
      if (challengeData && challengeData.progress && challengeData.progress.currentValue) {
        console.log(`[COMMENT CHALLENGE] Found saved comment count in challenge progress: ${challengeData.progress.currentValue}`);
        
        // Use the higher value between local count and saved count
        const savedCount = challengeData.progress.currentValue || 0;
        dbCount = Math.max(dbCount, savedCount);
      }
    } catch (e) {
      console.error("[COMMENT CHALLENGE] Error checking challenge progress:", e);
    }
    
    // Use the higher of the two counts to ensure we don't miss any comments
    const finalCount = Math.max(dbCount, commentsList.length);
    
    console.log(`[COMMENT CHALLENGE] Final count: ${finalCount} comments for user ${userId} in the specified period (DB: ${dbCount}, Local: ${commentsList.length})`);
    
    return finalCount;
  } catch (error) {
    console.error("[COMMENT CHALLENGE] Error in getCommentsCountWithinPeriod:", error);
    return 0;
  }
};

// Force add a comment for challenge tracking
export const forceAddCommentForChallenge = async (userId: string) => {
  try {
    if (!userId) {
      console.error("[COMMENT CHALLENGE] No user ID provided for forceAddCommentForChallenge");
      return false;
    }
    
    console.log(`[COMMENT CHALLENGE] Force adding comment for user ${userId}`);
    
    const now = new Date();
    
    // Create a dummy comment data
    const commentData = {
      recipeId: 'force-added',
      commentId: `force-added-${now.getTime()}`,
      timestamp: now.toISOString()
    };
    
    // Store in localStorage
    const commentTrackingKey = `challenge_comments_${userId}`;
    let commentsList = [];
    
    try {
      const storedComments = localStorage.getItem(commentTrackingKey);
      if (storedComments) {
        commentsList = JSON.parse(storedComments);
        if (!Array.isArray(commentsList)) {
          commentsList = [];
        }
      }
    } catch (e) {
      console.error("[COMMENT CHALLENGE] Error parsing stored comments:", e);
    }
    
    // Add the new comment to the list
    commentsList.push(commentData);
    
    // Store the updated list
    localStorage.setItem(commentTrackingKey, JSON.stringify(commentsList));
    
    console.log(`[COMMENT CHALLENGE] Force added comment. Total: ${commentsList.length}`);
    
    // Update session state
    const sessionChallengeKey = `challenge_state_${userId}_2`;
    try {
      const existingState = sessionStorage.getItem(sessionChallengeKey);
      if (existingState) {
        const parsedState = JSON.parse(existingState);
        if (parsedState.status === "in_progress") {
          const updatedState = {
            ...parsedState,
            currentValue: commentsList.length,
            progress: Math.min((commentsList.length / 3) * 100, 100)
          };
          sessionStorage.setItem(sessionChallengeKey, JSON.stringify(updatedState));
          localStorage.setItem(sessionChallengeKey, JSON.stringify(updatedState));
          console.log(`[COMMENT CHALLENGE] Updated session state after force add: ${JSON.stringify(updatedState)}`);
        }
      }
    } catch (e) {
      console.error("[COMMENT CHALLENGE] Error updating session state:", e);
    }
    
    // Get the current challenge progress
    const challengeData = await getChallengeProgress(userId, 2);
    
    // Prepare progress data to update
    const updatedProgress = {
      currentValue: commentsList.length,
      commentIds: commentsList.map(c => c.commentId),
      started_at: challengeData?.progress?.started_at || now.toISOString()
    };
    
    if (challengeData) {
      // Update the challenge progress in the database
      await updateChallengeProgress(userId, 2, updatedProgress);
      console.log(`[COMMENT CHALLENGE] Updated challenge 2 progress in database:`, updatedProgress);
    } else {
      // Start a new challenge if it doesn't exist
      await startChallenge(userId, 2, updatedProgress);
      console.log(`[COMMENT CHALLENGE] Started challenge 2 with initial progress:`, updatedProgress);
    }
    
    // Dispatch an event to notify other components
    const commentAddedEvent = new CustomEvent('comment-added', {
      detail: {
        userId,
        commentData,
        commentCount: commentsList.length,
        forceAdded: true
      }
    });
    window.dispatchEvent(commentAddedEvent);
    
    return true;
  } catch (error) {
    console.error("[COMMENT CHALLENGE] Error in forceAddCommentForChallenge:", error);
    return false;
  }
};

// Enhanced function to track ratings for challenges
export const trackRatingForChallenge = async (userId: string, ratingData: { recipeId: string, timestamp: string }) => {
  try {
    if (!userId) {
      console.error("[RATING CHALLENGE] No user ID provided for trackRatingForChallenge");
      return false;
    }
    
    console.log(`[RATING CHALLENGE] ★★★ Tracking rating for challenge. User: ${userId} ★★★`, ratingData);
    
    // Store in localStorage for immediate access
    const ratingTrackingKey = `challenge_ratings_${userId}`;
    let ratingsList = [];
    
    try {
      const storedRatings = localStorage.getItem(ratingTrackingKey);
      if (storedRatings) {
        ratingsList = JSON.parse(storedRatings);
        // Validate that it's an array
        if (!Array.isArray(ratingsList)) {
          console.error("[RATING CHALLENGE] Stored ratings is not an array, resetting");
          ratingsList = [];
        }
      }
    } catch (e) {
      console.error("[RATING CHALLENGE] Error parsing stored ratings:", e);
    }
    
    // Check if this recipe is already rated
    const existingRatingIndex = ratingsList.findIndex(r => r.recipeId === ratingData.recipeId);
    if (existingRatingIndex >= 0) {
      console.log(`[RATING CHALLENGE] Recipe ${ratingData.recipeId} already rated, updating timestamp`);
      // Update the timestamp
      ratingsList[existingRatingIndex].timestamp = ratingData.timestamp;
    } else {
      // Add the new rating to the list
      ratingsList.push(ratingData);
      console.log(`[RATING CHALLENGE] Added new rating for recipe ${ratingData.recipeId}`);
    }
    
    // Store the updated list
    localStorage.setItem(ratingTrackingKey, JSON.stringify(ratingsList));
    
    console.log(`[RATING CHALLENGE] Updated challenge ratings for user ${userId}. Total: ${ratingsList.length}`);
    
    // Update session state
    const sessionChallengeKey = `challenge_state_${userId}_4`;
    try {
      const existingState = sessionStorage.getItem(sessionChallengeKey);
      if (existingState) {
        const parsedState = JSON.parse(existingState);
        if (parsedState.status === "in_progress") {
          const updatedState = {
            ...parsedState,
            currentValue: ratingsList.length,
            progress: Math.min((ratingsList.length / 3) * 100, 100)
          };
          sessionStorage.setItem(sessionChallengeKey, JSON.stringify(updatedState));
          localStorage.setItem(sessionChallengeKey, JSON.stringify(updatedState));
          console.log(`[RATING CHALLENGE] Updated session state: ${JSON.stringify(updatedState)}`);
        }
      }
    } catch (e) {
      console.error("[RATING CHALLENGE] Error updating session state:", e);
    }
    
    // Get the current challenge progress
    const challengeData = await getChallengeProgress(userId, 4);
    if (challengeData) {
      // Make sure we update the database progress
      const currentProgress = challengeData.progress || {};
      const updatedProgress = {
        ...currentProgress,
        currentValue: ratingsList.length,
        ratingIds: ratingsList.map(r => r.recipeId)
      };
      
      // Update the challenge progress in the database
      await updateChallengeProgress(userId, 4, updatedProgress);
      console.log(`[RATING CHALLENGE] Updated challenge 4 progress in database:`, updatedProgress);
    } else {
      // Challenge doesn't exist yet, start it
      const initialProgress = {
        currentValue: ratingsList.length,
        ratingIds: ratingsList.map(r => r.recipeId),
        started_at: new Date().toISOString()
      };
      
      // Start the challenge with the initial progress
      await startChallenge(userId, 4, initialProgress);
      console.log(`[RATING CHALLENGE] Started challenge 4 with initial progress:`, initialProgress);
    }
    
    return true;
  } catch (error) {
    console.error("[RATING CHALLENGE] Error tracking rating for challenge:", error);
    return false;
  }
};

// Improved function to count ratings for challenges
export const getRatingsCountForChallenge = async (userId: string) => {
  try {
    if (!userId) {
      console.error("[RATING CHALLENGE] No user ID provided for getRatingsCountForChallenge");
      return 0;
    }
    
    console.log(`[RATING CHALLENGE] Counting ratings for user ${userId}`);
    
    // Get ratings from localStorage tracking
    const ratingTrackingKey = `challenge_ratings_${userId}`;
    let ratingsList = [];
    
    try {
      const storedRatings = localStorage.getItem(ratingTrackingKey);
      if (storedRatings) {
        const parsedRatings = JSON.parse(storedRatings);
        if (Array.isArray(parsedRatings)) {
          ratingsList = parsedRatings;
          console.log(`[RATING CHALLENGE] Found ${ratingsList.length} ratings in localStorage`);
        } else {
          console.error("[RATING CHALLENGE] Stored ratings is not an array", parsedRatings);
        }
      } else {
        console.log("[RATING CHALLENGE] No ratings found in localStorage");
      }
    } catch (e) {
      console.error("[RATING CHALLENGE] Error parsing stored ratings:", e);
    }
    
    // Check if we have any progress saved in the challenge
    try {
      const challengeData = await getChallengeProgress(userId, 4);
      if (challengeData && challengeData.progress && challengeData.progress.currentValue) {
        const savedCount = challengeData.progress.currentValue || 0;
        console.log(`[RATING CHALLENGE] Found ${savedCount} ratings in challenge progress`);
        
        // Use the higher count
        if (savedCount > ratingsList.length) {
          console.log(`[RATING CHALLENGE] Using saved count (${savedCount}) instead of local count (${ratingsList.length})`);
          return savedCount;
        }
      }
    } catch (e) {
      console.error("[RATING CHALLENGE] Error checking challenge progress:", e);
    }
    
    return ratingsList.length;
  } catch (error) {
    console.error("[RATING CHALLENGE] Error in getRatingsCountForChallenge:", error);
    return 0;
  }
};

// Force add a rating for the challenge
export const forceAddRatingForChallenge = async (userId: string) => {
  try {
    if (!userId) {
      console.error("[RATING CHALLENGE] No user ID provided for forceAddRatingForChallenge");
      return false;
    }
    
    console.log(`[RATING CHALLENGE] Force adding rating for user ${userId}`);
    
    const now = new Date();
    
    // Create a dummy rating data with a unique recipe ID
    const ratingData = {
      recipeId: `force-added-${now.getTime()}`,
      timestamp: now.toISOString()
    };
    
    // Add the rating using the normal tracking function
    const success = await trackRatingForChallenge(userId, ratingData);
    
    if (success) {
      // Dispatch an event to notify other components
      const ratingAddedEvent = new CustomEvent('recipe-rated', {
        detail: {
          userId,
          recipeId: ratingData.recipeId,
          isNew: true,
          forceAdded: true
        }
      });
      window.dispatchEvent(ratingAddedEvent);
      
      console.log("[RATING CHALLENGE] Successfully force added rating and dispatched event");
    }
    
    return success;
  } catch (error) {
    console.error("[RATING CHALLENGE] Error in forceAddRatingForChallenge:", error);
    return false;
  }
};
